/**
 * Birthdays and work anniversaries, for the dashboards.
 *
 * Today's celebrations are separated from the ones coming up, because they are
 * different asks: today is "say something to this person now", the rest is "it
 * is coming, do not be surprised". Sorting them into one list by date buries the
 * only row that needs acting on.
 *
 * The API returns month and day but never the birth year — see
 * lib/celebrations.js on the server — so there is no age to render here even by
 * accident.
 */
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { get } from '../lib/api.js';
import { Card, Avatar, Loading, Empty, Badge } from './ui.jsx';
import { Icon } from '../lib/icons.jsx';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "08-22" — a month and a day, with no year to parse. */
function whenLabel(on, inDays) {
  if (inDays === 0) return 'Today';
  if (inDays === 1) return 'Tomorrow';
  const [month, day] = on.split('-');
  return `${Number(day)} ${MONTHS[Number(month) - 1]}`;
}

function Row({ c, onOpen, prominent = false }) {
  const isBirthday = c.type === 'BIRTHDAY';
  return (
    <button
      type="button"
      onClick={() => onOpen(c.id)}
      className="flex items-c gap-10"
      style={{
        width: '100%',
        textAlign: 'left',
        padding: prominent ? '9px 10px' : '7px 0',
        borderRadius: prominent ? 10 : 0,
        border: 'none',
        borderBottom: prominent ? 'none' : '1px solid var(--border)',
        background: prominent ? 'var(--brand-wash)' : 'transparent',
        cursor: 'pointer',
        marginBottom: prominent ? 6 : 0,
      }}
    >
      <Avatar name={c.fullName} size={prominent ? 'md' : 'sm'} ring={prominent} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13.2 }}>{c.fullName}</div>
        <div className="txt-sm muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {isBirthday
            ? c.designation ?? 'Birthday'
            : `${c.years} year${c.years === 1 ? '' : 's'} at SevenMentor`}
        </div>
      </div>
      <div className="flex items-c gap-6" style={{ flex: 'none' }}>
        <Badge tone={isBirthday ? 'info' : 'good'} icon={isBirthday ? 'star' : 'award'}>
          {whenLabel(c.on, c.inDays)}
        </Badge>
      </div>
    </button>
  );
}

/**
 * @param {number} days  how far ahead to look
 * @param {number} limit how many upcoming rows to show; today's are never cut
 */
export default function Celebrations({ days = 30, limit = 5 }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['celebrations', days],
    queryFn: () => get('/reference/celebrations', { days }),
    staleTime: 600_000,
  });

  const open = (id) => navigate(`/employee/${id}`);

  const today = q.data?.data?.today ?? [];
  const upcoming = q.data?.data?.upcoming ?? [];

  return (
    <Card
      title="Birthdays & anniversaries"
      subtitle={today.length ? `${today.length} today` : `Next ${days} days`}
    >
      {q.isLoading ? <Loading /> : (
        <>
          {today.map((c) => (
            <Row key={`${c.id}-${c.type}`} c={c} onOpen={open} prominent />
          ))}

          {today.length > 0 && upcoming.length > 0 && (
            <div className="txt-sm muted" style={{ padding: '10px 0 4px', fontWeight: 600 }}>
              Coming up
            </div>
          )}

          {upcoming.slice(0, limit).map((c) => (
            <Row key={`${c.id}-${c.type}`} c={c} onOpen={open} />
          ))}

          {today.length === 0 && upcoming.length === 0 && (
            <Empty icon="star" title="Nothing coming up" sub={`No birthdays or anniversaries in the next ${days} days.`} />
          )}

          {upcoming.length > limit && (
            <div className="txt-sm muted mt-8">
              <Icon name="calendar" /> {upcoming.length - limit} more in the next {days} days
            </div>
          )}
        </>
      )}
    </Card>
  );
}
